// 2026-06-04 — Modal de cadastro rápido de cliente sem sair do ModalArt.
// Campos mínimos pra criar a entidade no Comercial. Após salvar, devolve o
// cliente criado pro caller (que dispara recarga da lista + auto-seleciona).

import { useState } from 'react';
import axios from 'axios';
import {
  Modal, Form, Input, Select, Button, Space, Typography, message, App as AntApp, Row, Col,
} from 'antd';
import { API_URL } from '../../config';
import { getToken } from './utils';

const { Text } = Typography;

interface Cliente {
  id: number;
  razao_social: string;
  cnpj: string | null;
  [k: string]: any;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  onCreated: (cli: Cliente) => void;
  // pré-preenche se já tem CNPJ digitado em outro lugar
  cnpjPreset?: string;
  // texto do botão / título — "Contratante" ou "Proprietário"
  tituloContexto?: string;
}

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

function ModalCadastrarClienteInner({ open, onCancel, onCreated, cnpjPreset, tituloContexto }: Props) {
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    try {
      const v = await form.validateFields();
      setSalvando(true);
      const token = await getToken();
      const payload: any = {
        razao_social: (v.razao_social || '').trim(),
      };
      if (v.nome_fantasia) payload.nome_fantasia = v.nome_fantasia.trim();
      if (v.cnpj) payload.cnpj = v.cnpj.replace(/\D/g, '').trim();
      if (v.telefone) payload.telefone = v.telefone.trim();
      if (v.email) payload.email = v.email.trim();
      if (v.endereco) payload.endereco = v.endereco.trim();
      if (v.bairro) payload.bairro = v.bairro.trim();
      if (v.cidade) payload.cidade = v.cidade.trim();
      if (v.uf) payload.uf = v.uf.trim();
      if (v.cep) payload.cep = v.cep.trim();
      if (v.eh_orgao_publico) payload.eh_orgao_publico = true;

      const r = await axios.post(`${API_URL}/comercial/clientes`, payload,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`Cliente "${d.razao_social}" cadastrado`);
      form.resetFields();
      onCreated(d as Cliente);
    } catch (e: any) {
      if (e.errorFields) return;  // validação Antd
      message.error('Falha ao cadastrar: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open={open} onCancel={() => !salvando && onCancel()}
      title={`Cadastrar empresa${tituloContexto ? ` (${tituloContexto})` : ''}`}
      width={680}
      confirmLoading={salvando}
      okText="Cadastrar" cancelText="Cancelar"
      onOk={salvar} destroyOnClose>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
        Preencha o essencial — você pode completar endereço/contatos depois em
        <strong> Comercial → Clientes</strong>.
      </Text>
      <Form form={form} layout="vertical" size="small"
        initialValues={{ cnpj: cnpjPreset || '' }}>
        <Form.Item label="Razão social" name="razao_social"
          rules={[{ required: true, message: 'Obrigatório' }]}>
          <Input placeholder="Ex: COMERCIAL ZAFFARI LTDA." />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Nome fantasia" name="nome_fantasia">
              <Input placeholder="Opcional" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="CNPJ" name="cnpj">
              <Input placeholder="00.000.000/0000-00" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Telefone" name="telefone">
              <Input placeholder="Opcional" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="E-mail" name="email">
              <Input placeholder="Opcional" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Endereço (logradouro + número)" name="endereco">
          <Input placeholder="Ex: Rua General Osório, 1426" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Bairro" name="bairro">
              <Input />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item label="Cidade" name="cidade">
              <Input placeholder="Ex: Passo Fundo" />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item label="UF" name="uf">
              <Select options={UFS.map(u => ({ value: u, label: u }))} allowClear showSearch />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="CEP" name="cep">
              <Input placeholder="00000-000" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="eh_orgao_publico" valuePropName="checked" style={{ marginBottom: 0 }}>
          <Space>
            <input type="checkbox" id="eh_op"
              onChange={(e) => form.setFieldValue('eh_orgao_publico', e.target.checked)} />
            <label htmlFor="eh_op" style={{ fontSize: 12, cursor: 'pointer' }}>
              É órgão público
            </label>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function ModalCadastrarCliente(props: Props) {
  return <AntApp><ModalCadastrarClienteInner {...props} /></AntApp>;
}
